<template>
  <div
    class="vch-full-width-slider"
    :class="{ 'vch-full-width-slider--with-disclaimers': withDisclaimers }"
    ref="container"
    :data-qa="qaHook"
  >
    <div
      class="vch-full-width-slider__content"
      :class="{ '-is-dragging': this.isDragging }"
      @mousedown="dragStart"
      ref="scrollable"
    >
      <slot />
    </div>
    <vch-carousel-indicator
      indicator-type="bar"
      item-selector=".vch-full-width-slider__item"
      :aria-pagination-label="paginationLabel"
      :aria-item-label="itemLabel"
      :observe-content="false"
      :current-item="currentItem"
      :use-anchor-scrolling="false"
      :on-indicator-click="scrollToItem"
    ></vch-carousel-indicator>
  </div>
</template>

<script>
import { debounce } from 'Utils/';

const scrollDebounceTimeout = 100;
let observer;

export default {
  name: 'VchFullWidthSlider',
  props: {
    paginationLabel: {
      type: String,
      required: false,
    },
    itemLabel: {
      type: String,
      required: false,
    },
    qaHook: {
      type: String,
      required: false,
    },
    withDisclaimers: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      isDragging: false,
      scrollableEl: null,
      microMoveMargin: 15,
      items: [],
      intersectingItemIndex: 0,
      currentItem: 0,
      cardWidth: 0,
    };
  },
  mounted() {
    this.scrollableEl = this.$slots.default?.[0].elm;

    this.items = this.$slots.default?.[0]?.children
      .filter((n) => n.tag !== undefined)
      .map((n) => n.elm);

    observer = new IntersectionObserver(this.updateIntersectingItemIndex, {
      threshold: 0.3,
    });

    this.items.forEach((item) => {
      observer.observe(item);
    });

    window.addEventListener('resize', this.updateCardWidths);

    // Updates the current card indicator when scrolling via touch devices
    this.$refs.scrollable.children[0].addEventListener(
      'touchend',
      this.handleTouchDrag
    );

    this.$nextTick(function () {
      this.updateCardWidths();
      this.scrollableEl.addEventListener(
        'scroll',
        debounce(this.onSliderScrolled, scrollDebounceTimeout)
      );
      this.setCurrentItemAttributes(0);
    });
  },
  beforeDestroy() {
    this.items.forEach((item) => {
      observer.unobserve(item);
    });
    this.scrollableEl.removeEventListener(
      'scroll',
      debounce(this.onSliderScrolled, scrollDebounceTimeout)
    );
    window.removeEventListener('resize', this.updateCardWidths);
  },
  methods: {
    updateCardWidths() {
      const containerWidth = this.$refs.container.offsetWidth,
        widthInColumns = 11,
        gutterWidth = 16;
      const gridColWidth =
        (containerWidth - (widthInColumns - 1) * gutterWidth) / 11;
      this.cardWidth =
        window.innerWidth < 1024
          ? containerWidth
          : gridColWidth * widthInColumns + (widthInColumns - 1) * gutterWidth;

      // Set the calculated width of each card (11/12 grid columns)
      this.items.forEach((item) => {
        item.style.minWidth = `${this.$refs.container.offsetWidth}px`;
      });

      // Set the width of the psuedo element spacer at the end of the carousel
      const contentMargin = window.innerWidth - containerWidth;
      this.scrollableEl.style.setProperty(
        '--end-spacing',
        `${contentMargin}px`
      );
    },
    dragStart(startEvent) {
      const startOffset = startEvent.clientX;
      const scrollStart = this.scrollableEl.scrollLeft;

      const dragMove = (moveEvent) => {
        moveEvent.preventDefault();
        const dx = moveEvent.clientX - startOffset;
        this.isDragging =
          this.isDragging || Math.abs(dx) > this.microMoveMargin;
        this.scrollableEl.scrollLeft = scrollStart - dx;
      };

      const dragStop = () => {
        this.scrollToItem(parseInt(this.intersectingItemIndex));

        // Give the smooth scroll a half second to finish before turning the snap back on.
        setTimeout(() => {
          this.isDragging = false;
        }, 500);

        this.$refs.scrollable.removeEventListener('mousemove', dragMove);
        this.$refs.scrollable.removeEventListener('mouseup', dragStop);
        this.$refs.scrollable.removeEventListener('mouseleave', dragStop);
        this.$refs.scrollable.removeEventListener('blur', dragStop);
      };

      this.$refs.scrollable.addEventListener('mousemove', dragMove);
      this.$refs.scrollable.addEventListener('mouseup', dragStop);
      this.$refs.scrollable.addEventListener('mouseleave', dragStop);
      this.$refs.scrollable.addEventListener('blur', dragStop);
    },
    updateIntersectingItemIndex([e]) {
      if (e.isIntersecting) {
        this.intersectingItemIndex = this.items.indexOf(e.target).toString();
      }
    },
    scrollToItem(index) {
      if (index < 0 || index > this.items.length - 1) return;
      this.scrollableEl?.scrollTo({
        top: 0,
        left: this.items[index].offsetLeft,
        behavior: 'smooth',
      });
      this.moveFocus(index);
    },
    moveFocus(itemIndex) {
      this.items.forEach((item, idx) => {
        const sliderContent = item.querySelector(
          '.vch-card-highlight__content'
        );
        if (sliderContent) {
          if (idx === itemIndex) {
            sliderContent.setAttribute('tabindex', 0);
            sliderContent.focus();
          } else {
            sliderContent.removeAttribute('tabindex');
          }
        }
      });
    },
    onSliderScrolled(e) {
      // Sets the current item based on the scroll offset
      this.currentItem = Math.round(e.target.scrollLeft / this.cardWidth);
      this.setCurrentItemAttributes(this.currentItem);
    },
    setCurrentItemAttributes(itemIndex) {
      this.items.forEach((item, idx) => {
        if (idx === itemIndex) {
          item.setAttribute('aria-hidden', 'false');
          item
            .querySelectorAll('a')
            .forEach((anchor) => anchor.removeAttribute('tabindex'));
        } else {
          item.setAttribute('aria-hidden', 'true');
          item
            .querySelectorAll('a')
            .forEach((anchor) => anchor.setAttribute('tabindex', '-1'));
        }
      });
    },
  },
};
</script>
<style lang="scss" src="./full-width-slider.scss"></style>
